import { useState } from 'react';
import { X } from 'lucide-react';
import useModalDialog from '../../utils/useModalDialog';
import { createSubscriberSubscription } from '../../utils/subscriptionApi';

export default function SubscriptionRegistration({ plans, selectedPlan, onSelect, isOpen, onClose }) {
  const dialogRef = useModalDialog(isOpen, '#subscription-plan');
  const [form, setForm] = useState({ name: '', email: '', mobile: '', consent: false });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ type: '', message: '' });
  const submitting = status.type === 'sending';
  const change = event => {
    const { name, value, checked, type } = event.target;
    setForm(current => ({ ...current, [name]: type === 'checkbox' ? checked : value }));
    setErrors(current => ({ ...current, [name]: '' }));
    setStatus({ type: '', message: '' });
  };
  const submit = async event => {
    event.preventDefault();
    if (submitting || status.type === 'success') return;
    const invalid = {};
    if (!form.name.trim()) invalid.name = 'Enter your name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) invalid.email = 'Enter a valid email address.';
    if (!/^\+?[0-9]{10,15}$/.test(form.mobile.trim())) invalid.mobile = 'Enter 10–15 digits, optionally starting with +.';
    if (!form.consent) invalid.consent = 'Consent is required to submit an enquiry.';
    if (!plans.some(plan => plan.id === selectedPlan)) invalid.plan = 'Select a plan.';
    setErrors(invalid);
    if (Object.keys(invalid).length) {
      setStatus({ type: 'error', message: 'Please check the highlighted fields.' });
      document.getElementById(`subscription-${Object.keys(invalid)[0]}`)?.focus();
      return;
    }
    const plan = plans.find(item => item.id === selectedPlan);
    setStatus({ type: 'sending', message: 'Sending your enquiry…' });
    try {
      const subscription = await createSubscriberSubscription(form, plan.id);
      setStatus({ type: 'success', message: `Your ${plan.name} subscription is active from ${subscription.startDate} through ${subscription.endDate}. No payment has been collected and there is no automatic renewal.` });
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Unable to send your enquiry. Please retry.' });
    }
  };
  return (
    <dialog ref={dialogRef} className="subscription-drawer" aria-labelledby="subscription-enquiry-title" aria-modal="true" onKeyDown={event => {
      if (event.key === 'Escape') { event.preventDefault(); onClose(); }
    }} onCancel={event => { event.preventDefault(); onClose(); }} onClick={event => {
      if (event.target !== event.currentTarget) return;
      const bounds = event.currentTarget.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) onClose();
    }}>
      <button type="button" className="subscription-drawer-close" aria-label="Close enquiry" onClick={onClose}><X size={24} aria-hidden="true" /></button>
      <section id="subscription-enquiry" className="subscription-registration" aria-labelledby="subscription-enquiry-title">
      <div><p className="subscription-eyebrow">START YOUR SUBSCRIPTION</p><h2 id="subscription-enquiry-title">Join your next chapter.</h2><p>Choose a plan and create your subscription for one calendar month, starting today.</p><p className="subscription-enquiry-note">Submitting activates your subscription. No payment is collected here and there is no automatic renewal.</p></div>
      <form noValidate onSubmit={submit}>
        <fieldset disabled={submitting || status.type === 'success'}>
          <legend className="subscription-sr-only">Your subscription enquiry</legend>
          <label htmlFor="subscription-plan">I'm interested in</label>
          <select id="subscription-plan" value={selectedPlan} aria-invalid={Boolean(errors.plan)} aria-describedby={errors.plan ? 'subscription-plan-error' : undefined} onChange={event => { onSelect(event.target.value); setStatus({ type: '', message: '' }); }}>
            {plans.map(plan => <option key={plan.id} value={plan.id}>{plan.name}</option>)}
          </select>
          {errors.plan && <span id="subscription-plan-error" className="subscription-field-error">{errors.plan}</span>}
          {[['name', 'Your name', 'text', 'name'], ['email', 'Email address', 'email', 'email'], ['mobile', 'Phone number', 'tel', 'tel']].map(([name, label, type, autoComplete]) => (
            <div key={name}>
              <label htmlFor={`subscription-${name}`}>{label}</label>
              <input id={`subscription-${name}`} name={name} type={type} autoComplete={autoComplete} value={form[name]} onChange={change} required aria-invalid={Boolean(errors[name])} aria-describedby={errors[name] ? `subscription-${name}-error` : undefined} />
              {errors[name] && <span id={`subscription-${name}-error`} className="subscription-field-error">{errors[name]}</span>}
            </div>
          ))}
          <label className="subscription-consent" htmlFor="subscription-consent"><input id="subscription-consent" name="consent" type="checkbox" checked={form.consent} onChange={change} required aria-invalid={Boolean(errors.consent)} aria-describedby={errors.consent ? 'subscription-consent-error' : undefined} /><span>I agree to create this subscription starting today and to be contacted about it.</span></label>
          {errors.consent && <span id="subscription-consent-error" className="subscription-field-error">{errors.consent}</span>}
          <button className="subscription-button" type="submit">{submitting ? 'Sending…' : 'Start subscription'}</button>
        </fieldset>
        {status.message && <p role={status.type === 'error' ? 'alert' : 'status'} className={`subscription-feedback subscription-feedback--${status.type}`}>{status.message}</p>}
      </form>
      </section>
    </dialog>
  );
}
